// contexts/MeasurementsContext.tsx
import { createContext, useContext, useEffect, useState } from "react";

interface Point {
  id?: number;
  isVisible?: boolean;
  [key: string]: any;
}

interface MeasurementItem {
  id: number;
  points: Point[];
  value: number | null;
  isVisible: boolean;
  isHidden: boolean;
}

interface MeasurementsContextType {
  addPoint: (point: any, setState: any) => void;
  distanceAndArea: MeasurementItem[];
  setDistanceAndArea: React.Dispatch<React.SetStateAction<MeasurementItem[]>>;
  volume: MeasurementItem[];
  setVolume: React.Dispatch<React.SetStateAction<MeasurementItem[]>>;
  setValue: (args: { type: number, payload: { id: number, value: number | null } }) => void;
  removeFigure: (args: { id: number }) => void;
  removePoint: (args: { id: number, index: number }) => void;
  setPointVisibility: (args: { id: number | null, index: number | null, isVisible: boolean | null }) => void;
  setVisibility: (args: { type: number, payload: { id: number | null, isVisible: boolean | null } }) => void;
  selectedTab: 0 | 1;
  setSelectedTab: React.Dispatch<React.SetStateAction<0 | 1>>;
  selectedFigure: number | 'none';
  setSelectedFigure: React.Dispatch<React.SetStateAction<number | 'none'>>;
  updatePointCoordinates: (args: { id: number, index: number, point: { x: number, y: number, z: number } }) => void;
  setIsHidden: (args: { type: number, payload: { id: number | null } }) => void;
}

const MeasurementsContext = createContext<MeasurementsContextType | undefined>(undefined);

export const MeasurementsProvider = ({ children }: { children: React.ReactNode }) => {
  const [distanceAndArea, setDistanceAndArea] = useState<MeasurementItem[]>(() => {
    const stored = sessionStorage.getItem('distanceAndArea');
    return stored ? JSON.parse(stored) : [];
  });

  const [volume, setVolume] = useState<MeasurementItem[]>(() => {
    const stored = sessionStorage.getItem('volume');
    return stored ? JSON.parse(stored) : [];
  });
  const [selectedFigure, setSelectedFigure] = useState<number | 'none'>('none');
  const [selectedTab, setSelectedTab] = useState<0 | 1>(0);

  useEffect(() => {
    sessionStorage.setItem('distanceAndArea', JSON.stringify(distanceAndArea));
  }, [distanceAndArea]);

  useEffect(() => {
    sessionStorage.setItem('volume', JSON.stringify(volume));
  }, [volume]);

  const addPoint = (
    point: any,
    setState: React.Dispatch<React.SetStateAction<any[]>>
  ): void => {
    setState(prevState =>
      prevState.map(item =>
        item.id === point.id
          ? {
            ...item,
            points: [...item.points, (({ id, ...rest }) => rest)(point)],
            value: item.value,
            isHidden: false,
          }
          : item
      )
    );
  };

  const setIsHidden = ({
    type,
    payload
  }: {
    type: number,
    payload: { id: number | null }
  }) => {
    // For distance/area (type 0)
    if (type === 0) {
      setDistanceAndArea(prevState =>
        prevState.map(item =>
          item.id === payload.id
            ? { ...item, isHidden: !item.isHidden } // Toggle the value
            : item
        )
      );
    }
    // For volume (type 1)
    else {
      setVolume(prevState =>
        prevState.map(item =>
          item.id === payload.id
            ? { ...item, isHidden: !item.isHidden } // Toggle the value
            : item
        )
      );
    }
  };

  const setValue = ({ type, payload }: { type: number, payload: { id: number, value: number | null } }) => {
    if (type === 0) {
      setDistanceAndArea(prevState =>
        prevState.map(item =>
          item.id === payload.id
            ? { ...item, value: payload.value }
            : item
        )
      );
    }
    if (type === 1) {
      setVolume(prev =>
        prev.map(item =>
          item.id === payload.id
            ? { ...item, value: payload.value }
            : item
        )
      );
    }
  };

  const removeFigure = ({ id }: { id: number }) => {
    selectedTab === 0
      ? setDistanceAndArea((prev) => prev.filter((item) => item.id !== id))
      : setVolume((prev) => prev.filter((item) => item.id !== id));
  };

  const removePoint = ({ id, index }: { id: number; index: number }) => {
    if (selectedTab === 0) {
      setDistanceAndArea(prevState =>
        prevState.map(item =>
          item.id === id
            ? {
              ...item,
              points: item.points.filter((_: any, pointIndex: number) => pointIndex !== index),
              value: item.points.length <= 1 ? null : item.value,
              isVisible: item.points.length <= 1 ? false : item.isVisible
            }
            : item
        )
      );
    } else {
      setVolume(prevState =>
        prevState.map(item =>
          item.id === id
            ? {
              ...item,
              points: item.points.filter((_: any, pointIndex: number) => pointIndex !== index),
              value: item.points.length <= 1 ? null : item.value,
              isVisible: item.points.length <= 1 ? false : item.isVisible
            }
            : item
        )
      );
    }
  };

  const setPointVisibility = ({
    id,
    index,
    isVisible
  }: {
    id: number | null;
    index: number | null;
    isVisible: boolean | null;
  }) => {
    setVolume(prevVolume =>
      prevVolume.map(item =>
        item.id === id
          ? {
            ...item,
            points: item.points.map((point: any, i: number) =>
              i === index ? { ...point, isVisible } : point
            )
          }
          : item
      )
    );
  };

  const setVisibility = ({
    type,
    payload
  }: {
    type: number,
    payload: { id: number | null, isVisible: boolean | null }
  }) => {
    // For distance/area (type 0)
    if (type === 0) {
      setDistanceAndArea((prevState: any) =>
        prevState.map((item: any) =>
          item.id === payload.id
            ? { ...item, isVisible: payload.isVisible }
            : item
        )
      );
    }
    // For volume (type 1)
    else {
      setVolume((prevState: any) =>
        prevState.map((item: any) =>
          item.id === payload.id
            ? { ...item, isVisible: payload.isVisible }
            : item
        )
      );
    }
  };

  const updatePointCoordinates = ({
    id,
    index,
    point
  }: {
    id: number;
    index: number;
    point: { x: number; y: number; z: number };
  }) => {
    if (selectedTab === 0) {
      setDistanceAndArea(prevState =>
        prevState.map(item =>
          item.id === id
            ? {
              ...item,
              points: item.points.map((p, i) =>
                i === index ? { ...p, ...point } : p
              )
            }
            : item
        )
      );
    } else {
      setVolume(prevState =>
        prevState.map(item =>
          item.id === id
            ? {
              ...item,
              points: item.points.map((p, i) =>
                i === index ? { ...p, ...point } : p
              )
            }
            : item
        )
      );
    }
  };

  return (
    <MeasurementsContext.Provider
      value={{
        addPoint,
        distanceAndArea,
        setDistanceAndArea,
        setIsHidden,
        volume,
        setVolume,
        setValue,
        removeFigure,
        removePoint,
        setPointVisibility,
        setVisibility,
        selectedTab,
        setSelectedTab,
        selectedFigure,
        setSelectedFigure,
        updatePointCoordinates
      }}
    >
      {children}
    </MeasurementsContext.Provider>
  );
};

export const useMeasurements = () => {
  const context = useContext(MeasurementsContext);
  if (context === undefined) {
    throw new Error('useMeasurements must be used within a MeasurementsProvider');
  }
  return context;
};